import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Plus, GripVertical, X, Users as UsersIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { DEFAULT_KEYWORD_CATEGORIES } from '../data/mockData';

const RoutineBuilderView = () => {
  const {
    user,
    users,
    userPresets,
    activeCrewId,
    setActiveCrew,
    getMyCrews,
    getCrewMembersSync,
    combos,
    shareContentToCrew,
    isContentSharedToCrew,
    getSharedItemsForCrew,
    createRoutine,
    routines,
    updateRoutineMeta,
    deleteRoutine,
    addParticipantToRoutine,
    removeParticipantFromRoutine,
    addSegmentToRoutine,
    removeSegmentFromRoutine,
    reorderRoutineSegments,
    duplicateSegment,
    setSegmentAssignments,
    updateSegmentVariations,
    updateSegmentSnapshot,
    canEditRoutine,
    arsenal,
    getCrewMembers
  } = useAuth();
  
  const myCrews = useMemo(() => getMyCrews(), [activeCrewId, user]);
  const [filters, setFilters] = useState({ authorId: 'all', signatureOnly: 'all', text: '', type: 'all' });
  const [newRoutine, setNewRoutine] = useState({ name: '', description: '' });
  const [selectedRoutineId, setSelectedRoutineId] = useState(null);

  const activeCrew = myCrews.find(c => c.id === activeCrewId) || myCrews[0] || null;
  const [crewMembers, setCrewMembers] = useState([]);
  const allUsers = users || [];

  // Load crew members when activeCrew changes
  useEffect(() => {
    const loadCrewMembers = async () => {
      if (activeCrew?.id) {
        console.log('🔄 Loading crew members for crew:', activeCrew.id);
        try {
          console.log('🔄 Calling getCrewMembers API...');
          const members = await getCrewMembers(activeCrew.id);
          console.log('✅ Crew members loaded:', members);
          console.log('✅ Crew members count:', members.length);
          setCrewMembers(members);
        } catch (e) {
          console.error('❌ Failed to load crew members:', e);
          console.error('❌ Error details:', e.message);
          setCrewMembers([]);
        }
      } else {
        console.log('🔄 No active crew, clearing crew members');
        setCrewMembers([]);
      }
    };
    loadCrewMembers();
  }, [activeCrew?.id, getCrewMembers]);

  // Debug: Log crew members data
  console.log('🎭 RoutineBuilderView - activeCrew:', activeCrew);
  console.log('🎭 RoutineBuilderView - crewMembers:', crewMembers);
  console.log('🎭 RoutineBuilderView - allUsers:', allUsers);
  console.log('🎭 RoutineBuilderView - user.moves:', user?.moves);
  const userVariations = (user?.customVariations && Object.keys(user.customVariations).length > 0)
    ? user.customVariations
    : DEFAULT_KEYWORD_CATEGORIES;

  const [variationsModalOpen, setVariationsModalOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [selectedItemForUsers, setSelectedItemForUsers] = useState(null);
  const [segmentAssignmentsModalOpen, setSegmentAssignmentsModalOpen] = useState(false);
  const [editingSegmentAssignments, setEditingSegmentAssignments] = useState(null);
  const [formationModalOpen, setFormationModalOpen] = useState(false);
  const [editingFormation, setEditingFormation] = useState(null); // { routineId, segmentId } | null
  const [formationDraft, setFormationDraft] = useState({
    name: '',
    description: '',
    rows: 6,
    cols: 4,
    timeUnit: 8,
    // cells: key "r-c" -> { count, moveId, moveName, itemType, variations: [] }
    cells: {},
    // finalPositions: userId -> cellKey (e.g., "1-2")
    finalPositions: {},
    // startPositions: userId -> cellKey
    startPositions: {},
  });
  const [selectedCellKey, setSelectedCellKey] = useState(null);
  const [formationFilters, setFormationFilters] = useState({ text: '', signatureOnly: 'all', type: 'all', dancerId: 'all' });
  const [formationPreviewOpen, setFormationPreviewOpen] = useState(false);
  const [formationPreview, setFormationPreview] = useState(null); // { name, timeUnit, matrix, participants }
  const [previewSelectedCellKey, setPreviewSelectedCellKey] = useState(null);
  const [selectedDancer, setSelectedDancer] = useState(null);
  const [placing, setPlacing] = useState(null); // { dancerId, step: 'start' | 'end' }
  // Routine playback
  const [routinePlaybackOpen, setRoutinePlaybackOpen] = useState(false);
  const [routinePlayback, setRoutinePlayback] = useState(null); // { segments: [...], participants: [...]} 
  const [playSegIdx, setPlaySegIdx] = useState(0);
  const [playUnit, setPlayUnit] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(1); // 0.5, 1, 2
  const [playFinished, setPlayFinished] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const playSegIdxRef = useRef(0);
  const playUnitRef = useRef(0);
  React.useEffect(() => { playSegIdxRef.current = playSegIdx; }, [playSegIdx]);
  React.useEffect(() => { playUnitRef.current = playUnit; }, [playUnit]);

  // Montrer tous les moves de l'utilisateur (pas seulement l'arsenal)
  const myMoves = (user?.moves || [])
    .map(m => ({ ...m, isSignature: !!m.isSignature }));
    
  // Mes contenus
  const ownItems = useMemo(() => ([
    ...myMoves.map(m => ({ ...m, itemType: 'move', authorId: user?.id, sourceId: m.id })),
    ...combos.map(c => ({ ...c, itemType: 'combo', authorId: user?.id, sourceId: c.id }))
  ]), [myMoves, combos, user]);
  // Contenus partagés par d'autres membres vers le crew actif
  const sharedItems = useMemo(() => {
    if (!activeCrew) return [];
    const shares = getSharedItemsForCrew(activeCrew.id) || [];
    return shares
      .filter(s => s.ownerUserId !== user?.id)
      .map(s => ({
        id: `${s.contentType}-${s.contentId}`,
        sourceId: s.contentId,
        itemType: s.contentType,
        authorId: s.ownerUserId,
        name: s.snapshot?.name,
        description: s.snapshot?.description,
        type: s.snapshot?.type,
        isSignature: !!s.snapshot?.isSignature,
        moves: s.snapshot?.moves || []
      }));
  }, [activeCrew, getSharedItemsForCrew, user]);

  const allItems = useMemo(() => ([...ownItems, ...sharedItems]), [ownItems, sharedItems]);


  // Filtrer: ne montrer que les moves/combos présents dans l'arsenal d'au moins un danseur du crew (pour moves seulement)
  const isInAnyArsenal = (moveId) => {
    // Vérifier dans l'arsenal de l'utilisateur connecté
    if (user?.moves?.some(m => m.id === moveId)) return true;
    
    // Vérifier dans les arsenaux des autres membres du crew
    const inCrewMembers = crewMembers.some(member => 
      (member.user?.arsenal || []).includes(moveId) || 
      (member.user?.moves || []).some(m => m.id === moveId)
    );
    
    // Debug temporaire pour le filtre Sylla
    if (filters.authorId === '68b95b91355f7c4a62e509e7') { // ID de Sylla
      console.log('🔍 Filter Sylla - moveId:', moveId);
      console.log('🔍 Filter Sylla - crewMembers:', crewMembers);
      console.log('🔍 Filter Sylla - inCrewMembers:', inCrewMembers);
    }
    
    return inCrewMembers;
  };

  const filteredItems = allItems.filter(item => {
    // Filtrage par auteur (All, Basics, Me, ou danseur spécifique)
    if (filters.authorId === 'basics') {
      if (item.itemType === 'move' && !item.isBasic) return false;
    } else if (filters.authorId !== 'all' && item.authorId !== filters.authorId) {
      return false;
    }
    
    if (filters.signatureOnly === 'signature' && !item.isSignature) return false;
    if (filters.signatureOnly === 'non-signature' && item.isSignature) return false;
    if (filters.type !== 'all' && item.itemType === 'move' && item.type !== filters.type) return false;
    if (filters.type !== 'all' && item.itemType === 'combo' && filters.type !== 'combo') return false;
    
    if (filters.text && !(`${item.name} ${item.description || ''}`.toLowerCase().includes(filters.text.toLowerCase()))) return false;
    return true;
  });

  const crewRoutines = (routines || []).filter(r => r.crewId === activeCrew?.id);
  const selectedRoutine = crewRoutines.find(r => r.id === selectedRoutineId) || null;

  const formationFilteredItems = useMemo(() => {
    let items = allItems;
    if (formationFilters.text) {
      const q = formationFilters.text.toLowerCase();
      items = items.filter(item => (`${item.name} ${item.description || ''}`).toLowerCase().includes(q));
    }
    if (formationFilters.signatureOnly === 'signature') items = items.filter(i => !!i.isSignature);
    if (formationFilters.signatureOnly === 'non-signature') items = items.filter(i => !i.isSignature);
    if (formationFilters.type !== 'all') {
      items = items.filter(i => (i.itemType === 'combo' && formationFilters.type === 'combo') || (i.itemType === 'move' && i.type === formationFilters.type));
    }
    if (formationFilters.dancerId !== 'all') {
      // Pour l'utilisateur connecté, utiliser ses moves
      if (formationFilters.dancerId === user?.id) {
        const userMoveIds = new Set((user?.moves || []).map(m => m.id));
        items = items.filter(i => i.itemType !== 'move' || userMoveIds.has(i.sourceId || i.id));
      } else {
        // Pour les autres membres du crew, on ne peut pas filtrer par leurs moves pour l'instant
        // car on n'a pas accès à leurs arsenaux
        items = items.filter(i => i.itemType !== 'move');
      }
    }
    return items;
  }, [allItems, formationFilters, user]);

  const openNewFormationModal = () => {
    setEditingFormation(null);
    setFormationDraft({ name: '', description: '', rows: 6, cols: 4, timeUnit: 8, cells: {}, finalPositions: {}, startPositions: {} });
    setSelectedCellKey('0-0');
    setFormationModalOpen(true);
  };

  const openEditFormationModal = (routineId, segment) => {
    const snap = segment.snapshot || {};
    const matrix = snap.matrix || {};
    setEditingFormation({ routineId, segmentId: segment.id });
    setFormationDraft({
      name: snap.name || '',
      description: snap.description || '',
      rows: matrix.rows || 6,
      cols: matrix.cols || 4,
      timeUnit: snap.timeUnit || 8,
      cells: matrix.cells || {},
      finalPositions: snap.finalPositions || {},
      startPositions: snap.startPositions || {},
    });
    // sélectionner une case par défaut: première avec contenus ou 0-0
    const keys = Object.keys(matrix.cells || {});
    const preferred = keys.find(k => {
      const cell = (matrix.cells || {})[k];
      return (cell && ((cell.count||0) > 0 || (Array.isArray(cell.assignments) && cell.assignments.length > 0)));
    });
    setSelectedCellKey(preferred || '0-0');
    setFormationModalOpen(true);
  };

  const toggleCellCount = (r, c, direction = 1) => {
    const key = `${r}-${c}`;
    setFormationDraft(prev => {
      const cell = prev.cells[key] || { count: 0, moveId: null, moveName: null, itemType: null, variations: [], assignments: [] };
      const max = 9; // limite simple pour n danseurs
      let next = (cell.count || 0) + direction;
      if (next > max) next = 0;
      if (next < 0) next = max;
      const nextAssignments = Array.isArray(cell.assignments) ? cell.assignments.slice(0, next) : [];
      return { ...prev, cells: { ...prev.cells, [key]: { ...cell, count: next, assignments: nextAssignments } } };
    });
  };

  const assignMoveToCell = (r, c, item) => {
    const key = `${r}-${c}`;
    setFormationDraft(prev => {
      const cell = prev.cells[key] || { count: 0, moveId: null, moveName: null, itemType: null, variations: [], assignments: [] };
      return { ...prev, cells: { ...prev.cells, [key]: { ...cell, moveId: item.sourceId || item.id, moveName: item.name, itemType: item.itemType, variations: cell.variations || [], assignments: Array.isArray(cell.assignments) ? cell.assignments : [] } } };
    });
  };

  const toggleVariationForCell = (r, c, v) => {
    const key = `${r}-${c}`;
    setFormationDraft(prev => {
      const cell = prev.cells[key] || { count: 0, moveId: null, moveName: null, itemType: null, variations: [], assignments: [] };
      const set = new Set(cell.variations || []);
      if (set.has(v)) set.delete(v); else set.add(v);
      return { ...prev, cells: { ...prev.cells, [key]: { ...cell, variations: Array.from(set) } } };
    });
  };

  const incrementSelectedCellCount = (delta) => {
    if (!selectedCellKey) return;
    const [r, c] = selectedCellKey.split('-').map(n => parseInt(n));
    toggleCellCount(r, c, delta);
  };

  const assignMoveToSelectedCell = (item) => {
    if (!selectedCellKey) return;
    const [r, c] = selectedCellKey.split('-').map(n => parseInt(n));
    assignMoveToCell(r, c, item);
  };

  const toggleVariationForSelectedCell = (v) => {
    if (!selectedCellKey) return;
    const [r, c] = selectedCellKey.split('-').map(n => parseInt(n));
    toggleVariationForCell(r, c, v);
  };

  // Removed per simplified UI: dancer assignment is handled exclusively via finalPositions

  const setFinalPositionForDancer = (userId, cellKey) => {
    setFormationDraft(prev => {
      const newFinalPositions = { ...prev.finalPositions };
      if (cellKey) {
        newFinalPositions[userId] = cellKey;
      } else {
        delete newFinalPositions[userId];
      }
      return { ...prev, finalPositions: newFinalPositions };
    });
  };

  const setStartPositionForDancer = (userId, cellKey) => {
    setFormationDraft(prev => {
      const next = { ...prev.startPositions };
      if (cellKey) {
        next[userId] = cellKey;
      } else {
        delete next[userId];
      }
      return { ...prev, startPositions: next };
    });
  };

  const getDancerCurrentPosition = (userId) => {
    return formationDraft.finalPositions[userId] || null;
  };

  const getDancerStartPosition = (userId) => {
    return formationDraft.startPositions[userId] || null;
  };

  const isPositionOccupied = (cellKey) => {
    return Object.values(formationDraft.finalPositions).includes(cellKey);
  };

  const isStartPositionOccupied = (cellKey) => {
    return Object.values(formationDraft.startPositions).includes(cellKey);
  };

  // UI helpers for names/initials
  const getUserDisplayName = (userId) => {
    // Chercher d'abord dans crewMembers (contient tous les membres du crew)
    const crewMember = crewMembers.find(m => m.userId === userId);
    if (crewMember?.user) {
      return crewMember.user.profile?.displayName || crewMember.user.username || crewMember.user.email || userId;
    }
    
    // Fallback vers users et user (pour l'utilisateur connecté)
    const u = users.find(u => u.id === userId) || ((user && user.id === userId) ? user : null);
    return u?.profile?.displayName || u?.username || u?.email || userId;
  };
  const toInitials = (name) => {
    if (!name) return '';
    const parts = String(name).trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
  };

  // Helpers: compute path and position per unit for playback
  const computePath = (startKey, endKey) => {
    if (!startKey || !endKey || startKey === endKey) return [];
    const [sr, sc] = startKey.split('-').map(n => parseInt(n));
    const [er, ec] = endKey.split('-').map(n => parseInt(n));
    const path = [];
    let r = sr, c = sc;
    // Move columns first, then rows (deterministic)
    while (c !== ec) { c += (ec > c ? 1 : -1); path.push(`${r}-${c}`); }
    while (r !== er) { r += (er > r ? 1 : -1); path.push(`${r}-${c}`); }
    return path; // sequence of keys after leaving start to reach end
  };

  const getPositionAtUnit = (startKey, endKey, timeUnit, unitIdx) => {
    if (!startKey) return endKey || null;
    if (!endKey) return startKey;
    const path = computePath(startKey, endKey);
    const steps = path.length;
    if (steps === 0) return startKey;
    // Show the end position on the last unit
    if (unitIdx >= Math.max(1, timeUnit) - 1) return endKey;
    const progress = Math.floor((unitIdx * steps) / Math.max(1, timeUnit));
    if (progress <= 0) return startKey;
    if (progress >= steps) return endKey;
    return path[progress - 1];
  };

  const saveFormationDraft = () => {
    const snapshot = {
      name: formationDraft.name || 'Formation',
      description: formationDraft.description || '',
      timeUnit: formationDraft.timeUnit,
      matrix: {
        rows: formationDraft.rows,
        cols: formationDraft.cols,
        cells: formationDraft.cells,
      },
      finalPositions: formationDraft.finalPositions,
      startPositions: formationDraft.startPositions,
    };
    if (editingFormation) {
      updateSegmentSnapshot({ routineId: editingFormation.routineId, segmentId: editingFormation.segmentId, snapshotUpdates: snapshot });
    } else if (selectedRoutine && canEditRoutine(selectedRoutine.id)) {
      addSegmentToRoutine({
        routineId: selectedRoutine.id,
        sourceType: 'formation',
        sourceOwnerUserId: user?.id,
        sourceId: `formation-${Date.now()}`,
        sourceVersion: 1,
        snapshot,
      });
    }
    setFormationModalOpen(false);
    setEditingFormation(null);
  };

  // Animation loop for routine playback
  const timerRef = useRef(0);
  React.useEffect(() => {
    if (!routinePlaybackOpen || !isPlaying) return;
    clearTimeout(timerRef.current || 0);
    const unitMsBase = 250;
    const tick = () => {
      const segments = routinePlayback?.segments || [];
      const seg = segments[playSegIdxRef.current];
      if (!seg) { setIsPlaying(false); return; }
      const segUnits = Math.max(1, seg.timeUnit || 8);
      const nextUnit = playUnitRef.current + 1;
      if (nextUnit >= segUnits) {
        const nextSeg = playSegIdxRef.current + 1;
        if (nextSeg >= segments.length) {
          // At the end: stop and show final end positions of the last segment
          setIsPlaying(false);
          setPlayFinished(true);
          // Keep last segment selected and force display of End by setting unit to last
          playSegIdxRef.current = segments.length - 1;
          setPlaySegIdx(segments.length - 1);
          playUnitRef.current = Math.max(0, segUnits - 1);
          setPlayUnit(Math.max(0, segUnits - 1));
          return;
        }
        playSegIdxRef.current = nextSeg;
        playUnitRef.current = 0;
        setPlaySegIdx(nextSeg);
        setPlayUnit(0);
        setPlayFinished(false);
      } else {
        playUnitRef.current = nextUnit;
        setPlayUnit(nextUnit);
        setPlayFinished(false);
      }
      const unitMs = unitMsBase / Math.max(0.25, playSpeed);
      timerRef.current = setTimeout(tick, unitMs);
    };
    const unitMs = unitMsBase / Math.max(0.25, playSpeed);
    timerRef.current = setTimeout(tick, unitMs);
    return () => clearTimeout(timerRef.current || 0);
  }, [routinePlaybackOpen, isPlaying, playSpeed, routinePlayback]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Routine Builder</h2>
        <div className="flex items-center gap-2">
          <Select value={activeCrewId || (activeCrew ? activeCrew.id : '')} onValueChange={setActiveCrew}>
            <SelectTrigger className="w-60">
              <SelectValue placeholder="Sélectionner un crew" />
            </SelectTrigger>
            <SelectContent>
              {myCrews.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {activeCrew && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="md:rounded-xl shadow border-2 bg-white text-black">
            <CardHeader className="bg-white text-black border-b-2 rounded-t-xl">
              <CardTitle>Library</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 bg-white text-black">
              {selectedRoutine && canEditRoutine(selectedRoutine.id) && (
                <div className="flex justify-end">
                  <Button onClick={openNewFormationModal} className="bg-black text-white border-2 border-black">Advanced mode</Button>
                </div>
              )}
              <div className="flex gap-2">
                <Select value={filters.authorId} onValueChange={v => setFilters(f => ({ ...f, authorId: v }))}>
                  <SelectTrigger className="w-40 bg-white text-black border-black"><SelectValue placeholder="Author" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="basics">Basics</SelectItem>
                    <SelectItem value={user?.id || ''}>Me</SelectItem>
                    {crewMembers.filter(m => m.userId !== user?.id).map(m => (
                      <SelectItem key={m.userId} value={m.userId}>{m.user?.profile?.displayName || m.user?.username || m.user?.email || m.userId}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.signatureOnly} onValueChange={v => setFilters(f => ({ ...f, signatureOnly: v }))}>
                  <SelectTrigger className="w-40 bg-white text-black border-black"><SelectValue placeholder="Signature" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="signature">Signature</SelectItem>
                    <SelectItem value="non-signature">Non-signature</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.type} onValueChange={v => setFilters(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="w-40 bg-white text-black border-black"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="combo">Combos</SelectItem>
                    <SelectItem value="toprock">TopRock</SelectItem>
                    <SelectItem value="footwork">Footwork</SelectItem>
                    <SelectItem value="powermoves">PowerMoves</SelectItem>
                    <SelectItem value="freezes">Freezes</SelectItem>
                    <SelectItem value="transitions">Transitions</SelectItem>
                    <SelectItem value="flips">Flips</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="Search..." value={filters.text} onChange={e => setFilters(f => ({ ...f, text: e.target.value }))} className="bg-white text-black border-black" />

              <div className="max-h-72 overflow-y-auto space-y-2">
                {filteredItems.map(item => (
                  <div key={`${item.itemType}-${item.id}`} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <Badge>{item.itemType}</Badge>
                      <span className="font-medium text-sm">{item.name}</span>
                      {item.isSignature && <Badge className="bg-yellow-200 text-yellow-900">Signature</Badge>}
                    </div>
                    <div className="flex items-center gap-2">
                      {item.itemType === 'move' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="px-2 py-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItemForUsers(item);
                            setUsersModalOpen(true);
                          }}
                        >
                          <UsersIcon className="w-4 h-4" />
                        </Button>
                      )}
                      {selectedRoutine && canEditRoutine(selectedRoutine.id) && (
                        <Button size="sm" onClick={() => {
                          const snapshot = { name: item.name, description: item.description, type: item.type, moves: item.moves || [] };
                          addSegmentToRoutine({
                            routineId: selectedRoutine.id,
                            sourceType: item.itemType,
                            sourceOwnerUserId: item.authorId,
                            sourceId: item.sourceId || item.id,
                            sourceVersion: 1,
                            snapshot
                          });
                        }}>Add</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="md:col-span-2 space-y-6">
            <Card className="border-2">
              <CardHeader className="bg-white text-black border-b-2">
                <CardTitle>Create a routine</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center gap-2 bg-white text-black">
                <Input placeholder="Name" value={newRoutine.name} onChange={e => setNewRoutine(n => ({ ...n, name: e.target.value }))} className="bg-white text-black border-black" />
                <Input placeholder="Description" value={newRoutine.description} onChange={e => setNewRoutine(n => ({ ...n, description: e.target.value }))} className="bg-white text-black border-black" />
                <Button className="bg-white text-black border-2 border-black hover:bg-gray-100" onClick={() => {
                  if (activeCrew && newRoutine.name.trim()) {
                    const r = createRoutine({ crewId: activeCrew.id, name: newRoutine.name, description: newRoutine.description, status: 'draft' });
                    setSelectedRoutineId(r.id);
                    setNewRoutine({ name: '', description: '' });
                  }
                }}>Create</Button>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader className="bg-white text-black border-b-2">
                <CardTitle>Crew routines</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 bg-white text-black">
                {crewRoutines.map(r => (
                  <div key={r.id} className={`flex flex-wrap  items-center justify-between p-2 border rounded ${selectedRoutineId === r.id ? 'bg-gray-50 dark:bg-gray-900' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{r.name}</span>
                      <Badge>{r.status}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="bg-white text-black border-black" onClick={() => setSelectedRoutineId(r.id)}>Open</Button>
                      <Button variant="outline" size="sm" className="bg-white text-black border-black" onClick={() => {
                        // Build playback data from routine segments
                        const segments = ([...(r.segments || [])].sort((a,b) => (a.orderIndex||0)-(b.orderIndex||0))).map(seg => {
                          if (seg.sourceType === 'formation') {
                            return {
                              type: 'formation',
                              name: seg.snapshot?.name || '',
                              timeUnit: seg.snapshot?.timeUnit || 8,
                              rows: seg.snapshot?.matrix?.rows || 6,
                              cols: seg.snapshot?.matrix?.cols || 4,
                              cells: seg.snapshot?.matrix?.cells || {},
                              startPositions: seg.snapshot?.startPositions || {},
                              finalPositions: seg.snapshot?.finalPositions || {},
                            };
                          }
                          return {
                            type: seg.sourceType,
                            name: seg.snapshot?.name || '',
                            timeUnit: seg.snapshot?.timeUnit || 8,
                          };
                        });
                        setRoutinePlayback({ segments, participants: r.participants || [] });
                        setPlaySegIdx(0);
                        setPlayUnit(0);
                        setIsPlaying(false);
                        setRoutinePlaybackOpen(true);
                      }}>Preview</Button>
                      {canEditRoutine(r.id) && <Button variant="destructive" size="sm" onClick={() => deleteRoutine(r.id)}>Remove</Button>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {selectedRoutine && (
              <Card className="border-2">
                <CardHeader className="bg-white text-black border-b-2">
                  <CardTitle>Edit: {selectedRoutine.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 bg-white text-black">
                  <div className="flex items-center gap-2">
                    <Select value={selectedRoutine.status} onValueChange={v => updateRoutineMeta(selectedRoutine.id, { status: v })}>
                      <SelectTrigger className="w-40 bg-white text-black border-black"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">draft</SelectItem>
                        <SelectItem value="published">published</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="text-sm text-gray-500">Créée par: {getUserDisplayName(selectedRoutine.createdByUserId)}</div>
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold">Participants</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedRoutine.participants.map(p => (
                        <Badge key={p.userId} className="flex items-center gap-2">
                          {getUserDisplayName(p.userId)}
                          {canEditRoutine(selectedRoutine.id) && p.userId !== selectedRoutine.createdByUserId && (
                            <button onClick={() => removeParticipantFromRoutine({ routineId: selectedRoutine.id, userId: p.userId })}><X className="w-3 h-3" /></button>
                          )}
                        </Badge>
                      ))}
                    </div>
                    {canEditRoutine(selectedRoutine.id) && (
                      <div className="flex items-center gap-2">
                        <Select onValueChange={uid => addParticipantToRoutine({ routineId: selectedRoutine.id, userId: uid })}>
                          <SelectTrigger className="w-60"><SelectValue placeholder="Add member" /></SelectTrigger>
                          <SelectContent>
                            {(() => {
                              console.log('🎭 Add Member - selectedRoutine:', selectedRoutine);
                              console.log('🎭 Add Member - selectedRoutine.participants:', selectedRoutine.participants);
                              console.log('🎭 Add Member - crewMembers:', crewMembers);
                              
                              // Debug: Log participant and crew member details
                              selectedRoutine.participants?.forEach((p, i) => {
                                console.log(`🎭 Participant ${i}:`, p);
                              });
                              crewMembers.forEach((m, i) => {
                                console.log(`🎭 Crew Member ${i}:`, m);
                              });
                              
                              const availableMembers = crewMembers.filter(m => !(selectedRoutine.participants || []).find(p => p.userId === m.userId));
                              console.log('🎭 Add Member - availableMembers:', availableMembers);
                              return availableMembers.map(m => (
                                <SelectItem key={m.userId} value={m.userId}>{m.user?.profile?.displayName || m.user?.username || m.user?.email || m.userId}</SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="font-semibold">Segments</div>
                    <div className="space-y-2">
                      {selectedRoutine.segments.map((s, idx) => (
                        <div key={s.id} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 border rounded-lg bg-gray-50">
                          <GripVertical className="w-4 h-4" />
                          <span className="text-sm w-6">{idx + 1}.</span>
                          <Badge>{s.sourceType}</Badge>
                          <span className="font-medium text-sm break-words max-w-full sm:max-w-[40%]">{s.snapshot?.name}</span>
                          {/* Preview visuelle pour formation */}
                          {s.sourceType === 'formation' && s.snapshot?.matrix && (
                            <div className="ml-0 sm:ml-2 w-full sm:w-auto">
                              <div className="text-xs text-gray-500">{s.snapshot?.timeUnit || 8} counts</div>
                              <button type="button" onClick={() => { setFormationPreview({ name: s.snapshot?.name, timeUnit: s.snapshot?.timeUnit || 8, matrix: s.snapshot.matrix || { rows: 0, cols: 0, cells: {} }, participants: selectedRoutine.participants || [], startPositions: s.snapshot?.startPositions || {}, finalPositions: s.snapshot?.finalPositions || {} }); setPreviewSelectedCellKey(null); setFormationPreviewOpen(true); }} className="mt-1 border" style={{ display: 'grid', gridTemplateColumns: `repeat(${s.snapshot.matrix.cols || 1}, 12px)`, gap: '2px' }}>
                                {Array.from({ length: (s.snapshot.matrix.rows || 0) * (s.snapshot.matrix.cols || 0) }).map((_, i) => {
                                  const r = Math.floor(i / (s.snapshot.matrix.cols || 1));
                                  const c = i % (s.snapshot.matrix.cols || 1);
                                  const key = `${r}-${c}`;
                                  const cell = (s.snapshot.matrix.cells || {})[key] || {};
                                  const startOwners = Object.entries(s.snapshot?.startPositions || {}).filter(([, pos]) => pos === key).map(([uid]) => uid);
                                  const endOwners = Object.entries(s.snapshot?.finalPositions || {}).filter(([, pos]) => pos === key).map(([uid]) => uid);
                                  const union = Array.from(new Set([...startOwners, ...endOwners]));
                                  const active = union.length > 0;
                                  const startNames = startOwners.map(uid => getUserDisplayName(uid));
                                  const endNames = endOwners.map(uid => getUserDisplayName(uid));
                                  const tooltip = [
                                    startNames.length ? `Start: ${startNames.join(', ')}` : '',
                                    endNames.length ? `End: ${endNames.join(', ')}` : '',
                                    cell.moveName ? `Move: ${cell.moveName}` : '',
                                    (cell.variations && cell.variations.length) ? `Variations: ${cell.variations.join(', ')}` : ''
                                  ].filter(Boolean).join('\n');
                                  let label = '';
                                  if (union.length === 1) {
                                    const name = getUserDisplayName(union[0]);
                                    label = (name || '').slice(0,3).toUpperCase();
                                  } else if (union.length > 1) {
                                    label = String(union.length);
                                  }
                                  return (
                                    <div key={i} className={`w-3 h-3 flex items-center justify-center text-[7px] ${active ? 'bg-black text-white' : 'bg-gray-200'}`} title={tooltip}>{label}</div>
                                  );
                                })}
                              </button>
                            </div>
                          )}
                          {Array.isArray(s.snapshot?.variations) && s.snapshot.variations.length > 0 && (
                            <div className="flex flex-wrap gap-1 ml-2">
                              {s.snapshot.variations.map((v, i) => (
                                <Badge key={i} className="bg-green-100 text-green-800 border-green-200 text-xs">{v}</Badge>
                              ))}
                            </div>
                          )}
                          {Array.isArray(s.assignments) && s.assignments.length > 0 && (
                            <div className="flex flex-wrap gap-1 ml-2">
                              {s.assignments.map((assignment, i) => {
                                const assignedUser = users.find(u => u.id === assignment.userId);
                                return (
                                  <Badge key={i} className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                                    {assignedUser?.username || assignedUser?.profile?.displayName || assignment.userId}
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                          <div className="ml-0 sm:ml-auto flex flex-wrap items-center gap-2 w-full sm:w-auto justify-start sm:justify-end">
                            {canEditRoutine(selectedRoutine.id) && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => reorderRoutineSegments({ routineId: selectedRoutine.id, fromIndex: idx, toIndex: Math.max(0, idx - 1) })}>↑</Button>
                                <Button size="sm" variant="outline" onClick={() => reorderRoutineSegments({ routineId: selectedRoutine.id, fromIndex: idx, toIndex: Math.min(selectedRoutine.segments.length - 1, idx + 1) })}>↓</Button>
                                {s.sourceType === 'formation' && (
                                  <Button size="sm" variant="outline" onClick={() => openEditFormationModal(selectedRoutine.id, s)}>Edit Formation</Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => duplicateSegment({ routineId: selectedRoutine.id, segmentId: s.id })}>Duplicate</Button>
                                <Button size="sm" variant="outline" onClick={() => { setEditingSegment({ routineId: selectedRoutine.id, segmentId: s.id, variations: s.snapshot?.variations || [] }); setVariationsModalOpen(true); }}>Variations</Button>
                                <Button size="sm" variant="outline" onClick={() => { setEditingSegmentAssignments({ routineId: selectedRoutine.id, segmentId: s.id, assignments: s.assignments || [] }); setSegmentAssignmentsModalOpen(true); }}>Assign</Button>
                                <Button size="sm" variant="destructive" onClick={() => removeSegmentFromRoutine({ routineId: selectedRoutine.id, segmentId: s.id })}>Remove</Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Variations Modal */}
      <Dialog open={variationsModalOpen} onOpenChange={setVariationsModalOpen}>
        <DialogContent className="sm:max-w-md bg-white text-black border-black">
          <DialogHeader>
            <DialogTitle>Edit Variations</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-gray-500">Click to toggle variations for this segment.</div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {Object.entries(userVariations).map(([category, variations]) => (
                <div key={category} className="space-y-2">
                  <div className="font-semibold text-sm capitalize">{category}</div>
                  <div className="flex flex-wrap gap-2">
                    {variations.map((v) => {
                      const selected = editingSegment?.variations?.includes(v);
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => {
                            setEditingSegment(prev => {
                              if (!prev) return prev;
                              const set = new Set(prev.variations || []);
                              if (set.has(v)) set.delete(v); else set.add(v);
                              return { ...prev, variations: Array.from(set) };
                            });
                          }}
                          className={`px-3 py-1 rounded-md text-xs font-medium ${selected ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setVariationsModalOpen(false)}>Cancel</Button>
              <Button onClick={() => {
                if (editingSegment) {
                  updateSegmentVariations({ routineId: editingSegment.routineId, segmentId: editingSegment.segmentId, variations: editingSegment.variations || [] });
                }
                setVariationsModalOpen(false);
              }}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Routine Playback Modal */}
      <Dialog open={routinePlaybackOpen} onOpenChange={(open) => { setRoutinePlaybackOpen(open); if (!open) { setIsPlaying(false); setPlayUnit(0); setPlaySegIdx(0); } }}>
        <DialogContent className="sm:max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-white text-black border-black">
          <DialogHeader>
            <DialogTitle>Preview Routine</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Controls */}
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => { setIsPlaying(p => !p); setPlayFinished(false); }}>{isPlaying ? 'Pause' : 'Play'}</Button>
              <Button size="sm" variant={playFinished ? 'default' : 'outline'} onClick={() => { setIsPlaying(false); setPlayUnit(0); setPlaySegIdx(0); setPlayFinished(false); }}>Restart</Button>
              <Button size="sm" variant="outline" onClick={() => { setIsPlaying(false); setPlayUnit(u => Math.max(0, u - 1)); }}>-1 unit</Button>
              <Button size="sm" variant="outline" onClick={() => { setIsPlaying(false); setPlayUnit(u => u + 1); }}>+1 unit</Button>
              <Button size="sm" variant="outline" onClick={() => { setIsPlaying(false); setPlaySegIdx(i => Math.max(0, i - 1)); setPlayUnit(0); }}>Prev seg</Button>
              <Button size="sm" variant="outline" onClick={() => { setIsPlaying(false); setPlaySegIdx(i => Math.min((routinePlayback?.segments?.length || 1) - 1, i + 1)); setPlayUnit(0); }}>Next seg</Button>
              <Select value={String(playSpeed)} onValueChange={v => setPlaySpeed(parseFloat(v))}>
                <SelectTrigger className="w-24"><SelectValue placeholder="Speed" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.5">0.5x</SelectItem>
                  <SelectItem value="1">1x</SelectItem>
                  <SelectItem value="2">2x</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Visualization */}
            {(() => {
              const seg = routinePlayback?.segments?.[playSegIdx];
              if (!seg) return <div className="text-sm text-gray-600">No segments</div>;
              const isFormation = seg.type === 'formation';
              const rows = isFormation ? seg.rows : Math.max(6, (formationDraft.rows || 6));
              const cols = isFormation ? seg.cols : Math.max(4, (formationDraft.cols || 4));

              // Compute dancer positions for the current unit
              const unit = playUnit;
              const dancerPositions = new Map(); // userId -> key
              const segUnits = Math.max(1, seg.timeUnit || 8);
              (routinePlayback?.participants || []).forEach(p => {
                if (isFormation) {
                  const startKey = seg.startPositions?.[p.userId] || null;
                  const endKey = seg.finalPositions?.[p.userId] || null;
                  const pos = getPositionAtUnit(startKey, endKey, segUnits, Math.min(unit, segUnits - 1));
                  dancerPositions.set(p.userId, pos);
                } else {
                  // Non-formation: hold last known position from previous formation end if available
                  const backscan = [...(routinePlayback?.segments || [])].slice(0, playSegIdx).reverse();
                  let last = null;
                  for (const s of backscan) {
                    if (s.type === 'formation') {
                      last = s.finalPositions?.[p.userId] || s.startPositions?.[p.userId] || last;
                      if (last) break;
                    }
                  }
                  dancerPositions.set(p.userId, last);
                }
              });

              // Build reverse mapping cell -> dancers
              const cellToDancers = new Map();
              for (const [uid, key] of dancerPositions.entries()) {
                if (!key) continue;
                const arr = cellToDancers.get(key) || [];
                arr.push(uid);
                cellToDancers.set(key, arr);
              }

              return (
                <div className="flex gap-4">
                  {/* Grid */}
                  <div className="space-y-2">
                    <div className="text-xs text-gray-600">Segment {playSegIdx + 1}/{routinePlayback?.segments?.length || 0} — Units: {seg.timeUnit} — Current unit: {unit}</div>
                    <div className="border inline-block" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 28px)`, gap: '4px' }}>
                      {Array.from({ length: rows * cols }).map((_, i) => {
                        const r = Math.floor(i / cols);
                        const c = i % cols;
                        const key = `${r}-${c}`;
                        const dancers = cellToDancers.get(key) || [];
                        const label = dancers.length === 1 ? toInitials(getUserDisplayName(dancers[0])) : (dancers.length ? String(dancers.length) : '');
                        const hasDancers = dancers.length > 0;
                        
                        return (
                          <div 
                            key={i} 
                            className={`w-7 h-7 border text-[10px] flex items-center justify-center cursor-pointer transition-colors ${
                              hasDancers ? 'bg-black text-white hover:bg-gray-800' : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                            onMouseEnter={() => setHoveredCell(key)}
                            onMouseLeave={() => setHoveredCell(null)}
                            title={dancers.map(uid => getUserDisplayName(uid)).join(', ')}
                          >
                            {label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Details Panel */}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-600 mb-2">Cell Details</div>
                    {hoveredCell ? (
                      <div className="p-3 border rounded bg-gray-50 space-y-2">
                        <div className="font-semibold text-sm">Cell {hoveredCell}</div>
                        
                        {/* Move/Combo Info */}
                        {isFormation && seg.cells?.[hoveredCell] && (
                          <div>
                            <div className="text-xs text-gray-600">Move/Combo:</div>
                            <div className="text-sm font-medium">{seg.cells[hoveredCell].moveName || '—'}</div>
                            {seg.cells[hoveredCell].variations?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {seg.cells[hoveredCell].variations.map(v => (
                                  <Badge key={v} className="bg-blue-100 text-blue-800 border-blue-200 text-xs">{v}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Dancers in this cell */}
                        {(() => {
                          const cellDancers = cellToDancers.get(hoveredCell) || [];
                          if (cellDancers.length === 0) {
                            return <div className="text-xs text-gray-500">No dancers in this cell</div>;
                          }
                          
                          return (
                            <div>
                              <div className="text-xs text-gray-600">Dancers ({cellDancers.length}):</div>
                              <div className="space-y-1">
                                {cellDancers.map(uid => {
                                  const dancer = routinePlayback?.participants?.find(p => p.userId === uid);
                                  const displayName = getUserDisplayName(uid);
                                  return (
                                    <div key={uid} className="flex items-center gap-2 text-sm">
                                      <div className="w-2 h-2 bg-black rounded-full"></div>
                                      <span>{displayName}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="p-3 border rounded bg-gray-50 text-xs text-gray-500">
                        Hover over a cell to see details
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
      {/* Users Modal */}
      <Dialog open={usersModalOpen} onOpenChange={setUsersModalOpen}>
        <DialogContent className="sm:max-w-md bg-white text-black border-black">
          <DialogHeader>
            <DialogTitle>Move Mastery</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm font-medium">
              {selectedItemForUsers?.name} - Mastered by:
            </div>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {crewMembers
                .filter(member => {
                  const moveId = selectedItemForUsers?.sourceId || selectedItemForUsers?.id;
                  // Vérifier si le membre a ce move dans son arsenal OU dans ses moves
                  return (member.user?.arsenal || []).includes(moveId) || 
                         (member.user?.moves || []).some(m => m.id === moveId);
                })
                .map(member => (
                  <div key={member.userId} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm">
                      {member.user?.profile?.displayName || member.user?.username || member.user?.email || member.userId}
                    </span>
                  </div>
                ))}
              {crewMembers.filter(member => {
                const moveId = selectedItemForUsers?.sourceId || selectedItemForUsers?.id;
                return (member.user?.arsenal || []).includes(moveId) || 
                       (member.user?.moves || []).some(m => m.id === moveId);
              }).length === 0 && (
                <div className="text-sm text-gray-500 text-center py-4">
                  No crew members have mastered this move yet.
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setUsersModalOpen(false)}>Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Segment Assignments Modal */}
      <Dialog open={segmentAssignmentsModalOpen} onOpenChange={setSegmentAssignmentsModalOpen}>
        <DialogContent className="sm:max-w-md bg-white text-black border-black">
          <DialogHeader>
            <DialogTitle>Assign Dancers to Segment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-gray-500">Select which dancers will execute this segment.</div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {crewMembers.map(member => {
                const isAssigned = editingSegmentAssignments?.assignments?.some(a => a.userId === member.userId);
                return (
                  <label key={member.userId} className="flex items-center gap-3 p-2 border rounded cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={isAssigned}
                      onChange={() => {
                        setEditingSegmentAssignments(prev => {
                          if (!prev) return prev;
                          const newAssignments = [...(prev.assignments || [])];
                          if (isAssigned) {
                            // Remove assignment
                            const index = newAssignments.findIndex(a => a.userId === member.userId);
                            if (index > -1) newAssignments.splice(index, 1);
                          } else {
                            // Add assignment
                            newAssignments.push({ userId: member.userId });
                          }
                          return { ...prev, assignments: newAssignments };
                        });
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">
                      {member.user?.username || member.user?.profile?.displayName || member.userId}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setSegmentAssignmentsModalOpen(false)}>Cancel</Button>
              <Button onClick={() => {
                if (editingSegmentAssignments) {
                  setSegmentAssignments({ 
                    routineId: editingSegmentAssignments.routineId, 
                    segmentId: editingSegmentAssignments.segmentId, 
                    assignments: editingSegmentAssignments.assignments || [] 
                  });
                }
                setSegmentAssignmentsModalOpen(false);
              }}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Formation Modal */}
      <Dialog open={formationModalOpen} onOpenChange={setFormationModalOpen}>
        <DialogContent className="sm:max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-white text-black border-black">
          <DialogHeader>
            <DialogTitle>{editingFormation ? 'Edit Formation' : 'Add Formation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input placeholder="Name" value={formationDraft.name} onChange={e => setFormationDraft(p => ({ ...p, name: e.target.value }))} className="bg-white text-black border-black" />
              <Input type="number" min={1} placeholder="Rows" value={formationDraft.rows} onChange={e => setFormationDraft(p => ({ ...p, rows: Math.max(1, parseInt(e.target.value || '1')) }))} className="bg-white text-black border-black" />
              <Input type="number" min={1} placeholder="Cols" value={formationDraft.cols} onChange={e => setFormationDraft(p => ({ ...p, cols: Math.max(1, parseInt(e.target.value || '1')) }))} className="bg-white text-black border-black" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <div className="col-span-2">
                <textarea placeholder="Description" value={formationDraft.description} onChange={e => setFormationDraft(p => ({ ...p, description: e.target.value }))} className="w-full h-20 p-2 border-2 border-black rounded bg-white text-black" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm">Unités:</span>
                <Select value={String(formationDraft.timeUnit)} onValueChange={v => setFormationDraft(p => ({ ...p, timeUnit: parseInt(v) }))}>
                  <SelectTrigger className="w-24 bg-white text-black border-black"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="8">8</SelectItem>
                    <SelectItem value="16">16</SelectItem>
                    <SelectItem value="32">32</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Editing grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-600 mb-2">Click to select a cell. Counts update dynamically from Start/End placements.</div>
                <div className="mb-2 flex items-center gap-2">
                  <div className="text-xs text-gray-600">Cell: {selectedCellKey ? selectedCellKey : 'none'}</div>
                </div>
                <div className="border inline-block" style={{ display: 'grid', gridTemplateColumns: `repeat(${formationDraft.cols}, 28px)`, gap: '4px' }}>
                  {Array.from({ length: formationDraft.rows * formationDraft.cols }).map((_, i) => {
                    const r = Math.floor(i / formationDraft.cols);
                    const c = i % formationDraft.cols;
                    const key = `${r}-${c}`;
                    const cell = formationDraft.cells[key] || {};
                    const startSet = new Set(Object.entries(formationDraft.startPositions || {}).filter(([, pos]) => pos === key).map(([uid]) => uid));
                    const endSet = new Set(Object.entries(formationDraft.finalPositions || {}).filter(([, pos]) => pos === key).map(([uid]) => uid));
                    const union = new Set([...startSet, ...endSet]);
                    const dynamicCount = union.size;
                    const active = dynamicCount > 0;
                    const startHere = Object.values(formationDraft.startPositions || {}).includes(key);
                    const endHere = Object.values(formationDraft.finalPositions || {}).includes(key);
                    const assignedIds = Array.isArray(cell.assignments) ? cell.assignments : [];
                    const assignedNames = (selectedRoutine?.participants || [])
                      .filter(p => assignedIds.includes(p.userId))
                      .map(p => {
                        const u = users.find(u => u.id === p.userId);
                        return u?.username || u?.profile?.displayName || p.userId;
                      });
                    const tooltip = [
                      assignedNames.length ? `Dancers: ${assignedNames.join(', ')}` : '',
                      cell.moveName ? `Move: ${cell.moveName}` : '',
                      (cell.variations && cell.variations.length) ? `Variations: ${cell.variations.join(', ')}` : ''
                    ].filter(Boolean).join('\n');
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          setSelectedCellKey(key);
                          if (!placing) return;
                          const dancerId = placing.dancerId;
                          if (placing.step === 'start') {
                            // Allow multiple dancers to start on the same cell
                            setStartPositionForDancer(dancerId, key);
                            setPlacing({ dancerId, step: 'end' });
                          } else if (placing.step === 'end') {
                            // Allow multiple dancers to end on the same cell
                            setFinalPositionForDancer(dancerId, key);
                            setPlacing(null); // finish placement
                          }
                        }}
                        className={`w-7 h-7 border text-xs font-bold relative cursor-pointer ${selectedCellKey === key ? 'ring-2 ring-blue-500' : ''} ${active ? 'bg-black text-white' : 'bg-gray-100 text-gray-700'}`}
                        title={tooltip || `(${r+1},${c+1})`}
                      >
                        {dynamicCount || ''}
                        {(startHere || endHere) && (
                          <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${startHere && endHere ? 'bg-purple-500' : startHere ? 'bg-blue-500' : 'bg-green-500'}`} title={`${startHere ? 'Start' : ''}${startHere && endHere ? ' & ' : ''}${endHere ? 'End' : ''}`}></span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-3">
                {/* Filters */}
                <div className="space-y-2 p-2 border rounded">
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Search..." value={formationFilters.text} onChange={e => setFormationFilters(f => ({ ...f, text: e.target.value }))} className="bg-white text-black border-black" />
                    <Select value={formationFilters.dancerId} onValueChange={v => setFormationFilters(f => ({ ...f, dancerId: v }))}>
                      <SelectTrigger className="bg-white text-black border-black"><SelectValue placeholder="Filter by dancer" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All dancers</SelectItem>
                        {selectedRoutine?.participants.map(p => (
                          <SelectItem key={p.userId} value={p.userId}>{users.find(u => u.id === p.userId)?.username || p.userId}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={formationFilters.signatureOnly} onValueChange={v => setFormationFilters(f => ({ ...f, signatureOnly: v }))}>
                      <SelectTrigger className="bg-white text-black border-black"><SelectValue placeholder="Signature" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="signature">Signature</SelectItem>
                        <SelectItem value="non-signature">Non-signature</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={formationFilters.type} onValueChange={v => setFormationFilters(f => ({ ...f, type: v }))}>
                      <SelectTrigger className="bg-white text-black border-black"><SelectValue placeholder="Type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="combo">Combos</SelectItem>
                        <SelectItem value="toprock">TopRock</SelectItem>
                        <SelectItem value="footwork">Footwork</SelectItem>
                        <SelectItem value="powermoves">PowerMoves</SelectItem>
                        <SelectItem value="freezes">Freezes</SelectItem>
                        <SelectItem value="transitions">Transitions</SelectItem>
                        <SelectItem value="flips">Flips</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="text-sm font-semibold">Assign move/combo to selected cell</div>
                <div className="text-xs text-gray-600">Select a cell, then pick an item below to assign it.</div>
                <div className="max-h-40 overflow-y-auto space-y-1 border p-2 rounded">
                  {formationFilteredItems.slice(0, 100).map(item => {
                    const currentCell = selectedCellKey ? (formationDraft.cells[selectedCellKey] || {}) : {};
                    const isSelected = !!selectedCellKey && currentCell.moveId === (item.sourceId || item.id);
                    return (
                      <div key={`${item.itemType}-${item.id}`} className={`flex items-center justify-between p-1 border rounded ${isSelected ? 'bg-black text-white border-black' : ''}`}>
                        <div className="flex items-center gap-2">
                          <Badge className={isSelected ? 'bg-white text-black' : ''}>{item.itemType}</Badge>
                          <span className="text-sm">{item.name}</span>
                        </div>
                        <Button size="sm" variant={isSelected ? 'secondary' : 'outline'} onClick={() => assignMoveToSelectedCell(item)} disabled={!selectedCellKey}>{isSelected ? 'Selected' : 'Assign'}</Button>
                      </div>
                    );
                  })}
                </div>
                <div className="text-sm font-semibold">Variations (applied to selected cell)</div>
                <div className="max-h-32 overflow-y-auto space-y-2">
                  {Object.entries(userVariations).map(([cat, vars]) => (
                    <div key={cat}>
                      <div className="text-xs font-bold capitalize mb-1">{cat}</div>
                      <div className="flex flex-wrap gap-1">
                        {vars.map(v => {
                          const selected = !!selectedCellKey && (formationDraft.cells[selectedCellKey]?.variations || []).includes(v);
                          return (
                            <button key={v} type="button" onClick={() => toggleVariationForSelectedCell(v)} disabled={!selectedCellKey} className={`px-2 py-0.5 text-xs rounded border ${selected ? 'bg-green-600 text-white border-green-700' : ''} disabled:opacity-50`}>
                              {v}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Sélection courante */}
                <div className="p-2 border rounded">
                  <div className="text-sm font-semibold mb-1">Current cell</div>
                  {selectedCellKey ? (
                    <div className="space-y-1">
                      <div className="text-xs text-gray-600">{selectedCellKey}</div>
                      {(() => {
                        const cell = formationDraft.cells[selectedCellKey] || {};
                        const startOwners = Object.entries(formationDraft.startPositions || {}).filter(([, pos]) => pos === selectedCellKey).map(([uid]) => uid);
                        const endOwners = Object.entries(formationDraft.finalPositions || {}).filter(([, pos]) => pos === selectedCellKey).map(([uid]) => uid);
                        const startNames = startOwners.map(uid => getUserDisplayName(uid));
                        const endNames = endOwners.map(uid => getUserDisplayName(uid));
                        return (
                          <>
                            <div className="text-sm">{cell.moveName ? `Move: ${cell.moveName}` : 'No move selected'}</div>
                            {Array.isArray(cell.variations) && cell.variations.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {cell.variations.map(v => (
                                  <Badge key={v} className="bg-green-100 text-green-800 border-green-200 text-xs">{v}</Badge>
                                ))}
                              </div>
                            )}
                            <div className="text-xs text-gray-700">
                              Start: {startNames.length ? startNames.join(', ') : '—'}
                            </div>
                            <div className="text-xs text-gray-700">
                              End: {endNames.length ? endNames.join(', ') : '—'}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-600">No cell selected</div>
                  )}
                </div>
                {/* Dancer assignment to cells removed: handled via Final Positions below */}
              </div>
            </div>

            {/* Unified placement controls */}
            <div className="border-t pt-4">
              <div className="text-sm font-semibold mb-2">Placement</div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Select value={selectedDancer || ''} onValueChange={v => { 
                  setSelectedDancer(v || null); 
                  setPlacing(null); 
                  if (v) {
                    const prevEnd = (formationDraft.finalPositions || {})[v];
                    const prevStart = (formationDraft.startPositions || {})[v];
                    setSelectedCellKey(prevEnd || prevStart || null);
                  }
                }}>
                  <SelectTrigger className="w-56 bg-white text-black border-black"><SelectValue placeholder="Choose a dancer" /></SelectTrigger>
                  <SelectContent>
                    {selectedRoutine?.participants.map(p => (
                      <SelectItem key={p.userId} value={p.userId}>{getUserDisplayName(p.userId)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {placing && (
                  <div className="text-xs text-gray-600">
                    Placing {getUserDisplayName(placing.dancerId)}: click {placing.step === 'start' ? 'start' : 'end'} cell
                  </div>
                )}
                {selectedDancer && !placing && (
                  <div className="text-xs text-gray-600">
                    Start: {getDancerStartPosition(selectedDancer) || '—'} · End: {getDancerCurrentPosition(selectedDancer) || '—'}
                  </div>
                )}
                {selectedDancer && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setPlacing({ dancerId: selectedDancer, step: 'start' })}>Re-place</Button>
                    <Button size="sm" variant="destructive" onClick={() => { setStartPositionForDancer(selectedDancer, null); setFinalPositionForDancer(selectedDancer, null); }}>Clear Start/End</Button>
                  </div>
                )}
              </div>
              <div className="text-xs text-gray-600">Click a cell to set Start, then End. After End is set, selection mode exits automatically.</div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setFormationModalOpen(false); setEditingFormation(null); }}>Cancel</Button>
              <Button onClick={saveFormationDraft}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Formation Preview (mobile-friendly) */}
      <Dialog open={formationPreviewOpen} onOpenChange={(open) => { setFormationPreviewOpen(open); if (!open) setPreviewSelectedCellKey(null); }}>
        <DialogContent className="sm:max-w-md w-[95vw] max-h-[90vh] overflow-y-auto bg-white text-black border-black">
          <DialogHeader>
            <DialogTitle>Formation Preview{formationPreview?.name ? ` — ${formationPreview.name}` : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-gray-600">{formationPreview?.timeUnit} counts</div>
            <div className="border inline-block" style={{ display: 'grid', gridTemplateColumns: `repeat(${formationPreview?.matrix?.cols || 0}, 20px)`, gap: '4px' }}>
              {Array.from({ length: (formationPreview?.matrix?.rows || 0) * (formationPreview?.matrix?.cols || 0) }).map((_, i) => {
                const rows = formationPreview?.matrix?.rows || 0;
                const cols = formationPreview?.matrix?.cols || 0;
                const r = Math.floor(i / (cols || 1));
                const c = i % (cols || 1);
                const key = `${r}-${c}`;
                const cell = (formationPreview?.matrix?.cells || {})[key] || { moveName: null, variations: [] };
                const startOwners = Object.entries(formationPreview?.startPositions || {}).filter(([, pos]) => pos === key).map(([uid]) => uid);
                const endOwners = Object.entries(formationPreview?.finalPositions || {}).filter(([, pos]) => pos === key).map(([uid]) => uid);
                const union = Array.from(new Set([...startOwners, ...endOwners]));
                const active = union.length > 0;
                const isSelected = previewSelectedCellKey === key;
                let label = '';
                if (union.length === 1) {
                  const name = getUserDisplayName(union[0]);
                  label = (name || '').slice(0,3).toUpperCase();
                } else if (union.length > 1) {
                  label = String(union.length);
                }
                return (
                  <button key={i} type="button" onClick={() => setPreviewSelectedCellKey(key)} title={cell.moveName || ''} className={`w-5 h-5 text-[10px] flex items-center justify-center ${isSelected ? 'ring-2 ring-blue-500' : ''} ${active ? 'bg-black text-white' : 'bg-gray-200'}`}>{label}</button>
                );
              })}
            </div>
            <div className="space-y-2">
              {!previewSelectedCellKey ? (
                <div className="text-xs text-gray-600">Tap a cell above to see details (dancers, move, variations).</div>
              ) : (
                (() => {
                  const cell = (formationPreview?.matrix?.cells || {})[previewSelectedCellKey] || { moveName: null, variations: [] };
                  const startOwners = Object.entries(formationPreview?.startPositions || {}).filter(([, pos]) => pos === previewSelectedCellKey).map(([uid]) => uid);
                  const endOwners = Object.entries(formationPreview?.finalPositions || {}).filter(([, pos]) => pos === previewSelectedCellKey).map(([uid]) => uid);
                  const startNames = startOwners.map(uid => getUserDisplayName(uid));
                  const endNames = endOwners.map(uid => getUserDisplayName(uid));
                  return (
                    <div className="p-2 border rounded bg-gray-50">
                      <div className="text-sm font-semibold mb-1">Cell {previewSelectedCellKey}</div>
                      <div className="text-sm mb-1">Move: {cell.moveName || '—'}</div>
                      {Array.isArray(cell.variations) && cell.variations.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1">
                          {cell.variations.map(v => (
                            <Badge key={v} className="bg-green-100 text-green-800 border-green-200 text-xs">{v}</Badge>
                          ))}
                        </div>
                      )}
                      <div className="text-xs text-gray-700">Start: {startNames.length ? startNames.join(', ') : '—'}</div>
                      <div className="text-xs text-gray-700">End: {endNames.length ? endNames.join(', ') : '—'}</div>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoutineBuilderView;

